import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { CSVFieldType } from './CSVModalSelector/CsvModalAISelector.js'
import { identifyCSVSuggestion } from './CSVModalSelector/CsvModalAISelector.js'
import type { CSVMappingFlags, CSVMappingResult, CSVMode } from './CSVModalTypes.js'
import type { IOrder, ISamplePool } from '../../../../Model/index.js'
import { CSVColSelector } from './CSVColSelector.js'
import { CSVEnumSelector } from './MetadataHandling/ForEnums/CSVEnumSelector.js'
import { CSVDateTimeSelector } from './MetadataHandling/ForDateTime/CSVDateTimeSelector.js'
import {
  CollectionTypeConfig,
  ExtractionKitConfig,
  HabitatTypeConfig,
  LocalityTypeConfig,
  MatrixTypeConfig,
  SequencerConfig,
  SequencingChemistryConfig,
  TrapTypeConfig
} from './MetadataHandling/ForEnums/MetadataEnumConfig.js'
import { SamplingDateConfig, SamplingEndConfig, SamplingStartConfig } from './MetadataHandling/ForDateTime/DateTimeConfig.js'
import type { PureMetadata } from '../../../../Model/Sample/ISampleMetaData.js'
import type { CSVColumnSelectorReturn } from './MetadataHandling/CSVColumnSelectorReturn.js'
import type { CommonConfig } from './MetadataHandling/CSVCommonState.js'
import { CSVStringSelector } from './MetadataHandling/ForStrings/CSVStringSelector.js'
import { SequencingDepthConfig } from './MetadataHandling/ForStrings/MetadataStringConfig.js'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.js'
import { ValidateStartAndEndSamplingDate } from './MetadataHandling/CrossValidation/ValidateStartAndEndSamplingDate.js'
import { ValidateHabitatAndLocationAndTransform } from './MetadataHandling/CrossValidation/ValidateHabitatAndLocation.js'
import { CrossValidationDisplay } from './MetadataHandling/CrossValidation/CrossValidationDisplay.js'
import { useGetSamplePoolSelector } from '../../../../Flux/Selector/index.js'
import { CrossValidationErrorDisplay } from './MetadataHandling/CrossValidation/CrossValidationErrorDisplay.js'
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid/index.js'
import type { InvalidLatLonEntry } from './MetadataHandling/LatLonValidation.js'
import { findInvalidLatLonValues, sampleCoordinatesFromRow } from './MetadataHandling/LatLonValidation.js'
import type { CustomMetadataState } from './CustomMetadataSection.js'
import { CustomMetadataSection } from './CustomMetadataSection.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'
import { CLEAR_SENTINEL } from './MetadataHandling/CSVClearSentinel.js'
import { SheetStructureSection, useSheetStructure } from './SheetStructureSection.tsx'
import type { SheetColumnsUsed } from './PlanCsvImport.ts'

export interface ParseCSVModalProps {
  rawCSVData: Record<string, string>[]
  mode: CSVMode
  order?: IOrder
  /** The rows come from the data grid, so each one already names its sample in the `sampleId` column. */
  fromGrid?: boolean
}

export const GRID_SAMPLE_ID_COLUMN = 'sampleId'

/**
 * Which columns identify a row's sample before the user picks. Grid rows carry the sample's own id, which
 * matches exactly; the file-name prefix is ambiguous and is empty for single-file (merged/ONT) samples.
 */
export const initialIdentifierColumns = (
  headers: string[],
  data: Record<string, string>[],
  fromGrid: boolean
): { sequencingCol: string; sampleIdCol: string } => {
  if (fromGrid && headers.includes(GRID_SAMPLE_ID_COLUMN)) {
    return { sequencingCol: '', sampleIdCol: GRID_SAMPLE_ID_COLUMN }
  }
  return { sequencingCol: fromSuggestion('sequenceId', data, headers), sampleIdCol: '' }
}

export interface ParseCSVModalResult {
  data: CSVMappingResult[]
  flags: CSVMappingFlags
  /** Which type and pool columns the rows' `itemType` and `pool` came from. */
  sheet?: SheetColumnsUsed
}

export type MetadataState = Record<string, CSVColumnSelectorReturn>

type CoordinateFormat = 'single' | 'split'

const getCSVHeaders = (rawCSVData: Record<string, string>[]) => Object.keys(rawCSVData[0]).filter((d) => !!d.trim())

export function fromSuggestion(type: CSVFieldType, data: Record<string, string>[], headers: string[]): string {
  console.groupCollapsed('fromSuggestion for type', type)
  const suggestion = identifyCSVSuggestion(type, headers, data)
  if (suggestion) {
    console.info('Using suggestion for type', type, suggestion)
  }
  console.groupEnd()
  return suggestion ? suggestion[0] : ''
}

export function ParseCSVModal(props: ParseCSVModalProps & BaseModalProps<ParseCSVModalResult>) {
  const [CSVHeaders, setCSVHeaders] = useState<string[]>([])
  const [forwardPrimerCol, setForwardPrimerCol] = useState('')
  const [reversePrimerCol, setReversePrimerCol] = useState('')
  const [sequencingCol, setSequenceingCol] = useState('')
  const [customerSampleIdCol, setCustomerSampleIdCol] = useState('')
  const [sampleIdCol, setSampleIdCol] = useState('')

  const [coordinateFormat, setCoordinateFormat] = useState<CoordinateFormat>('split')
  const [coordinatesCol, setCoordinatesCol] = useState<string>('')
  const [latCol, setLatCol] = useState<string>('')
  const [lonCol, setLonCol] = useState<string>('')
  const [overrideCustomerPrimer, setOverrideCustomerPrimer] = useState<boolean>(true)
  const pools = useGetSamplePoolSelector()
  const [showOnlyAnalysisData, setShowOnlyAnalysisData] = useState<boolean>(props.mode === 'CREATE_SAMPLES')
  const [uniqueSampleId, setUniqueSampleId] = useState<string | undefined>(undefined)
  const [invalidLatLon, setInvalidLatLon] = useState<InvalidLatLonEntry[]>([])
  const [renderInstead, setRenderInstead] = useState<ReactNode | undefined>(undefined)
  const [commonMetadataState, setCommonMetadataState] = useState<MetadataState>({})
  const [customMetadataState, setCustomMetadataState] = useState<CustomMetadataState>({
    selectedColumns: []
  })
  const poolList = useMemo(() => (pools === 'LOADING' ? [] : pools), [pools])
  const structureEnabled = props.mode === 'CREATE_SAMPLES' && pools !== 'LOADING'
  const identity = { sampleIdCol, sequencingCol, customerSampleIdCol }
  const sheetStructure = useSheetStructure({
    enabled: structureEnabled,
    rows: props.rawCSVData,
    headers: CSVHeaders,
    pools: poolList,
    identity,
    order: props.order
  })

  useEffect(() => {
    if (
      props.rawCSVData?.length > 0 &&
      ((latCol && lonCol && coordinateFormat === 'split') || (coordinatesCol && coordinateFormat === 'single'))
    ) {
      const invalids = findInvalidLatLonValues(props.rawCSVData, coordinateFormat, latCol, lonCol, coordinatesCol)
      console.info('invalids', invalids)
      setInvalidLatLon(invalids)
    } else {
      setInvalidLatLon([])
    }
  }, [latCol, lonCol, coordinatesCol, coordinateFormat, props.rawCSVData, showOnlyAnalysisData])

  useEffect(() => {
    if (pools !== 'LOADING' && props.order?.sampleConfiguration === 'COMBINED_SAMPLE_PAIRS') {
      setUniqueSampleId(getUniqueSampleIdIfAvailable())
    }
  }, [pools])

  useEffect(() => {
    setShowOnlyAnalysisData(props.mode === 'CREATE_SAMPLES')
  }, [props.mode])

  const getUniqueSampleIdIfAvailable = (): string | undefined => {
    const allSamples = (pools === 'LOADING' ? [] : pools).flatMap((p) => p.samples)
    if (allSamples.length === 1) return allSamples[0].id
    return undefined
  }

  const handleCrossDependenciesAndMutateResult = async (result: CSVMappingResult[]) => {
    setRenderInstead(<CrossValidationDisplay />)

    let accumulatedErrors: string[] = []
    /* run checks validating dependencies between fields */
    accumulatedErrors = [...accumulatedErrors, ...ValidateStartAndEndSamplingDate(commonMetadataState, result)]
    accumulatedErrors = [...accumulatedErrors, ...ValidateHabitatAndLocationAndTransform(commonMetadataState, result)]
    console.info(accumulatedErrors)
    if (accumulatedErrors) {
      setRenderInstead(<CrossValidationErrorDisplay onClose={() => setRenderInstead(undefined)} errors={accumulatedErrors} />)
    } else {
      setRenderInstead(undefined)
    }

    return accumulatedErrors.length <= 0
  }

  /**
   * [overrideCustomerData] decides whether the import may touch a field that already holds a value:
   * with it off, only empty fields are filled. It is passed in by the button that was pressed rather
   * than read from a checkbox, so the choice cannot be made once at the top of a long form and
   * forgotten by the time the import is committed — on a finished order, where every field is already
   * set, the wrong one makes the whole import a no-op.
   */
  /** Both commit buttons share one set of preconditions; only the override choice differs. */
  const submitDisabled =
    Object.values(commonMetadataState).filter((x) => x.validationState === 'INVALID_VALUES').length > 0 ||
    (props.mode === 'UPDATE_METADATA' && !sequencingCol && !sampleIdCol && !customerSampleIdCol) ||
    (props.mode === 'CREATE_SAMPLES' && !sequencingCol && !sampleIdCol && !uniqueSampleId) ||
    (props.mode === 'PREPARE_METADATA' && !customerSampleIdCol)

  const handleOKClicked = async (overrideCustomerData: boolean) => {
    const result = csvResult()
    /* attention: does _not_ only validate, but may also mutate result for dependent columns*/
    if (await handleCrossDependenciesAndMutateResult(result)) {
      props.onClose({
        data: result,
        flags: {
          overrideCustomerData,
          overrideCustomerPrimer
        },
        sheet: structureEnabled ? sheetStructure.columnsUsed() : undefined
      })
    }
  }

  const sequencingDisabled = (pools: ISamplePool[]) => {
    const fwdMap: Record<string, boolean> = {}
    const revMap: Record<string, boolean> = {}
    for (const pool of pools) {
      for (const sample of [...pool.samples, ...pool.negativeControls]) {
        if (fwdMap[sample.forwardFileId] || (sample.reverseFileId && revMap[sample.reverseFileId])) {
          return true
        }
        fwdMap[sample.forwardFileId] = true
        if (sample.reverseFileId) {
          revMap[sample.reverseFileId] = true
        }
      }
    }
    return false
  }

  const handleMetadataChanged = (config: CommonConfig, event: CSVColumnSelectorReturn) => {
    setCommonMetadataState((prev) => {
      return {
        ...prev,
        [config.label]: event
      }
    })
  }

  useEffect(() => {
    if (props.rawCSVData) setCSVHeaders(getCSVHeaders(props.rawCSVData))
  }, [props.rawCSVData])

  useEffect(() => {
    console.groupCollapsed('ParseCSVModal initialize selects')
    /* apply 'ai' guessing */
    if (props.rawCSVData) {
      setForwardPrimerCol(fromSuggestion('forwardPrimer', props.rawCSVData, CSVHeaders))
      setReversePrimerCol(fromSuggestion('reversePrimer', props.rawCSVData, CSVHeaders))
      const identifiers = initialIdentifierColumns(CSVHeaders, props.rawCSVData, props.fromGrid ?? false)
      setSequenceingCol(identifiers.sequencingCol)
      setSampleIdCol(identifiers.sampleIdCol)
      setCustomerSampleIdCol(fromSuggestion('customerSample', props.rawCSVData, CSVHeaders))
      const mergeSuggestion = fromSuggestion('coordinatesMerged', props.rawCSVData, CSVHeaders)
      if (mergeSuggestion) {
        console.log('using type single, as I found a coordinatesMerged match')
        setCoordinateFormat('single')
        setCoordinatesCol(mergeSuggestion)
      } else {
        console.log('using type split, as I did not find a coordinatesMerged match')
        setCoordinateFormat('split')
        setLatCol(fromSuggestion('coordinatesLat', props.rawCSVData, CSVHeaders))
        setLonCol(fromSuggestion('coordinatesLon', props.rawCSVData, CSVHeaders))
      }
      console.log('sequence id after this effect', sequencingCol)
    }
    console.groupEnd()
  }, [CSVHeaders, props.rawCSVData])

  const csvResult: () => CSVMappingResult[] = () => {
    console.log(props.rawCSVData)
    return props.rawCSVData.map((d, index) => {
      const sampleCoordinates = sampleCoordinatesFromRow(d, coordinateFormat, latCol, lonCol, coordinatesCol)

      // Process metadata from all configured metadata selectors
      const metadataFromSelectors: PureMetadata = {}
      const clearedFields = new Set<keyof PureMetadata>()

      // Process each metadata entry from commonMetadataState
      Object.entries(commonMetadataState).forEach(([, value]) => {
        value.transform({
          row: d,
          result: metadataFromSelectors,
          clear: (fields) => fields.forEach((f) => clearedFields.add(f))
        })
      })

      return {
        metadata: showOnlyAnalysisData
          ? {}
          : {
              sampleCoordinates,
              /* add custom attributes */
              customAttributes: customMetadataState.selectedColumns
                .filter((x) => x !== null)
                .reduce((acc, cur) => ({ ...acc, [cur]: d[cur] }), {}),
              ...metadataFromSelectors
            },
        clearedFields: showOnlyAnalysisData ? undefined : [...clearedFields],
        sequenceId: sequencingCol ? d[sequencingCol] : undefined,
        customerSampleName: customerSampleIdCol ? d[customerSampleIdCol] : undefined,
        forwardPrimer: forwardPrimerCol ? d[forwardPrimerCol] : undefined,
        reversePrimer: reversePrimerCol ? d[reversePrimerCol] : undefined,
        sampleId: sampleIdCol ? d[sampleIdCol] : uniqueSampleId,
        ...sheetStructure.sheetValues(index)
      }
    })
  }

  const determineHint = () => {
    switch (props.mode) {
      case 'PREPARE_METADATA':
        return (
          <>
            <p>Upload CSV data to prepare metadata for lab usage. Customer Sample Name is mandatory and must be shared with the lab. </p>
            <p>We have tried to prefill some common columns.</p>
          </>
        )
      case 'CREATE_SAMPLES':
        return (
          <>
            <p>Upload CSV data to merge fastq pairs to samples. Sequence ID is mandatory and is used to identify the fastq pairs. </p>
            <p>If customer provided metadata is already present, take care to use matching customer sample names. </p>
            <p>We have tried to prefill some common columns.</p>
          </>
        )
      case 'UPDATE_METADATA':
        return (
          <>
            <p>Update sample metadata by providing the sample id or the sequence id to uniquely identify the sample to update. </p>
            <p>Analysis relevant data like primers can not be changed. </p>
            <p>We have tried to prefill some common columns.</p>
          </>
        )
    }
    return undefined
  }

  return (
    <BaseModal isOpen={props.isOpen} onClose={props.onClose}>
      <div className="modal-box relative w-fit max-w-[80vw]">
        {/* hook for the CSVMetadataSelector elements to conditionally render their own modal if needed */}
        {renderInstead && renderInstead}

        <div className={renderInstead ? 'hidden' : 'flex justify-between flex-col max-h-[80vh]'}>
          <h1 className="font-bold text-xl mb-2">
            <TooltipTrigger tooltip={determineHint()} withIcon={true}>
              Please select the appropriate Data to import
            </TooltipTrigger>
          </h1>

          <div className={'overflow-y-auto max-h-[80vh] shrink px-4'}>
            <hr className="mt-2 mb-1" />
            {props.mode === 'CREATE_SAMPLES' && uniqueSampleId && props.order?.sampleConfiguration === 'COMBINED_SAMPLE_PAIRS' ? (
              <label className="mb-4 font-semibold break-all">
                Since you are using combined sample pairs and only one sample pair has been uploaded,
                <br />
                spcfy will create samples from that without needing an id column.
              </label>
            ) : (
              <label className="mb-2 font-semibold">
                <TooltipTrigger
                  tooltip={
                    'These columns in your csv are used to uniquely identify which sample the corresponding row refers to. One of these must be present, otherwise your data cannot be mapped. If provided, spcfy sample id has precedence over all other options.'
                  }
                  withIcon
                >
                  Mandatory Columns
                </TooltipTrigger>
              </label>
            )}
            <hr className={'mt-2 mb-1'} />
            <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
              {props.mode === 'PREPARE_METADATA' && (
                /* if prepare metadata, sample name is mandatory */
                <CSVColSelector
                  label={'Customer Sample Name'}
                  testId="customer"
                  onChange={(e) => setCustomerSampleIdCol(e)}
                  value={customerSampleIdCol}
                  options={CSVHeaders}
                  mandatoryValues={[customerSampleIdCol]}
                  unwrapped
                />
              )}
              {
                /* if update metadata, sample id or sequence id or name must be provided */
                (props.mode === 'UPDATE_METADATA' ||
                  (props.mode === 'CREATE_SAMPLES' &&
                    !(props.order?.sampleConfiguration === 'COMBINED_SAMPLE_PAIRS' && uniqueSampleId))) && (
                  <>
                    {sequencingDisabled(pools === 'LOADING' ? [] : pools) && (
                      <>
                        <div></div>
                        <div className={'text-sm font-bold'}>
                          Sequence ids are non unique since samples have been re-used. Spcfy will attempt to duplicate samples to reuse
                          fastq files for demultiplexing.
                        </div>
                      </>
                    )}
                    <CSVColSelector
                      disabled={props.mode === 'UPDATE_METADATA' && sequencingDisabled(pools === 'LOADING' ? [] : pools)}
                      tooltip={'The common prefix used in your .fastq file names to identify a sample.'}
                      label={'Sequencing ID'}
                      testId="sequence"
                      onChange={(e) => setSequenceingCol(e)}
                      value={sequencingCol}
                      options={CSVHeaders}
                      mandatoryValues={[sequencingCol, sampleIdCol, customerSampleIdCol]}
                      unwrapped
                    />
                    <div className={'font-bold text-sm flex justify-center items-center mb-2'}>OR</div>
                    <div></div>
                    <CSVColSelector
                      label={'Spcfy Sample ID'}
                      testId="sampleid"
                      onChange={(e) => setSampleIdCol(e)}
                      value={sampleIdCol}
                      options={CSVHeaders}
                      mandatoryValues={[sequencingCol, sampleIdCol, customerSampleIdCol]}
                      unwrapped
                    />
                    {props.mode === 'UPDATE_METADATA' && (
                      <>
                        <div className={'font-bold text-sm flex justify-center items-center mb-2'}>OR</div>
                        <div></div>
                        <CSVColSelector
                          label={'Customer Sample Name'}
                          testId="customer"
                          onChange={(e) => setCustomerSampleIdCol(e)}
                          value={customerSampleIdCol}
                          options={CSVHeaders}
                          mandatoryValues={[sequencingCol, sampleIdCol, customerSampleIdCol]}
                          unwrapped
                        />
                      </>
                    )}
                  </>
                )
              }
            </div>
            {props.mode !== 'UPDATE_METADATA' && (
              <>
                <hr className="mt-2 mb-1" />
                <label className="mb-2 font-semibold">Naming and Primers</label>
                <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
                  {props.mode === 'CREATE_SAMPLES' && (
                    <CSVColSelector
                      label={'Customer Sample Name'}
                      testId="customer"
                      onChange={(e) => setCustomerSampleIdCol(e)}
                      value={customerSampleIdCol}
                      options={CSVHeaders}
                      unwrapped
                    />
                  )}
                  <CSVColSelector
                    label={'Forward Primer'}
                    onChange={(e) => setForwardPrimerCol(e)}
                    value={forwardPrimerCol}
                    options={CSVHeaders}
                    unwrapped
                  />
                  <CSVColSelector
                    label={'Reverse Primer'}
                    onChange={(e) => setReversePrimerCol(e)}
                    value={reversePrimerCol}
                    options={CSVHeaders}
                    unwrapped
                  />
                </div>
              </>
            )}
            {structureEnabled && (
              <SheetStructureSection state={sheetStructure} headers={CSVHeaders} />
            )}
            <div className={'flex gap-2 align-center items-center py-2'}>
              <input
                type="checkbox"
                className="checkbox checkbox-primary"
                checked={showOnlyAnalysisData}
                onChange={() => setShowOnlyAnalysisData(!showOnlyAnalysisData)}
              ></input>
              <label className="text-sm text-gray-500">
                <TooltipTrigger
                  tooltip={
                    'Shows/Hides fields used to import metadata. It is highly recommended to enrich your samples with as much metadata as possible, but metadata is not required initially to start the bioninformatics pipeline.'
                  }
                  withIcon
                >
                  Show only analysis relevant data. I will provide metadata later.
                </TooltipTrigger>
              </label>
            </div>
            <hr />
            {!showOnlyAnalysisData && (
              <>
                <div className="flex flex-col mb-2">
                  <label className="mb-1 font-semibold">Coordinates</label>
                  <div className="flex gap-2">
                    <label className="flex items-center gap-1">
                      <input type="radio" checked={coordinateFormat === 'single'} onChange={() => setCoordinateFormat('single')} />
                      Single Column (e.g. &quot;lat,lon&quot;)
                    </label>
                    <label className="flex items-center gap-1">
                      <input type="radio" checked={coordinateFormat === 'split'} onChange={() => setCoordinateFormat('split')} />
                      Separate Lat/Lon Columns
                    </label>
                  </div>
                </div>
                <CSVColSelector
                  label={'Sample Coordinates'}
                  onChange={(e) => setCoordinatesCol(e)}
                  value={coordinatesCol}
                  options={CSVHeaders}
                  disabled={coordinateFormat !== 'single'}
                />
                <CSVColSelector
                  label={'Latitude Column'}
                  onChange={(e) => setLatCol(e)}
                  value={latCol}
                  options={CSVHeaders}
                  disabled={coordinateFormat !== 'split'}
                />
                <CSVColSelector
                  label={'Longitude Column'}
                  onChange={(e) => setLonCol(e)}
                  value={lonCol}
                  options={CSVHeaders}
                  disabled={coordinateFormat !== 'split'}
                />
                {invalidLatLon.length > 0 && (
                  <div className="text-sm text-yellow-600 mt-1 flex items-center gap-1">
                    <ExclamationTriangleIcon className="w-5 h-5" />
                    <TooltipTrigger
                      tooltip={
                        coordinateFormat === 'split'
                          ? `Found ${invalidLatLon.length} row(s) with invalid latitude/longitude values.`
                          : `Found ${invalidLatLon.length} row(s) with invalid coordinates (expected format "lat,lon").`
                      }
                      withIcon={false}
                    >
                      Some coordinate values are invalid
                    </TooltipTrigger>
                  </div>
                )}
                <hr className="mt-2 mb-1" />
                <label className="mb-2 font-semibold">More Metadata</label>
                <div className="grid grid-cols-[auto_auto_auto_1fr] gap-2 items-center">
                  <div className="row-span-3 w-6 h-full flex py-4 items-center justify-center">
                    <div className={'border-l-2 border-t-2 border-b-2 h-full grow'}></div>
                  </div>
                  <CSVEnumSelector
                    config={LocalityTypeConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                  />
                  <div className="col-span-3 flex items-center text-gray-500 mt-1 mb-1">
                    <span className="text-xl"></span>
                    <TooltipTrigger
                      tooltip={
                        'Each Habitat belongs to a locality. When specifying both, take care to specify the correct locality for the habitat type. If only the habitat is specified, the correct locality will be auto-filled'
                      }
                      withIcon
                    >
                      <span>Habitats are classified under Localities</span>
                    </TooltipTrigger>
                  </div>

                  <CSVEnumSelector
                    config={HabitatTypeConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                  />
                  <span />
                  <CSVEnumSelector
                    config={CollectionTypeConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                  />
                  <span />
                  <CSVEnumSelector
                    config={ExtractionKitConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                  />
                  <span />
                  <CSVEnumSelector
                    config={MatrixTypeConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                  />
                  <span />
                  <CSVEnumSelector
                    config={SequencerConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                  />
                  <span />
                  <CSVEnumSelector
                    config={SequencingChemistryConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                  />
                  <span />
                  <CSVStringSelector
                    config={SequencingDepthConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                  />
                  <span />
                  <CSVEnumSelector
                    config={TrapTypeConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                  />
                  <span />
                  <CSVDateTimeSelector
                    config={SamplingDateConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                    hasSpanningColumnBefore={false}
                  />
                  {commonMetadataState[SamplingStartConfig.label]?.validationState !== 'NO_DATA' ? (
                    <div className="row-span-3 w-6 h-full flex py-4 items-center justify-center">
                      <div className={'border-l-2 border-t-2 border-b-2 h-full grow'}></div>
                    </div>
                  ) : (
                    <div className="row-span-2 w-6 h-full flex py-4 items-center justify-center">
                      <div className={'border-l-2 border-t-2 border-b-2 h-full grow'}></div>
                    </div>
                  )}
                  <CSVDateTimeSelector
                    config={SamplingStartConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                    hasSpanningColumnBefore={true}
                  />
                  <CSVDateTimeSelector
                    config={SamplingEndConfig}
                    onChange={handleMetadataChanged}
                    rawCSVData={props.rawCSVData}
                    csvHeaders={CSVHeaders}
                    renderInstead={(n) => setRenderInstead(n)}
                    hasSpanningColumnBefore={false}
                  />
                </div>
                <CustomMetadataSection state={customMetadataState} setState={setCustomMetadataState} csvHeaders={CSVHeaders} />
              </>
            )}
          </div>
          <label className="mb-2 font-semibold">Options</label>
          {/* Shown unconditionally: this used to render only once overriding was switched on, so the
              one place that teaches the token was behind the switch a reader did not know to look for. */}
          {/* Capped, not because of readability: the dialog is `w-fit`, so an uncapped one-line
              paragraph is the widest child and sets the whole dialog's width. */}
          <div className="text-xs text-gray-500 pb-2 max-w-xl" data-testid="clear-sentinel-hint">
            To empty a stored value, put <code className="font-mono font-semibold">{CLEAR_SENTINEL}</code> in the cell
            and import with <span className="font-semibold">Import &amp; overwrite</span>. An empty cell leaves the
            stored value alone, so a column that is only filled in for some rows cannot wipe the rest.
          </div>
          {props.mode === 'CREATE_SAMPLES' && (
            <>
              <div className={'flex gap-2 align-center items-center'}>
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary"
                  checked={overrideCustomerPrimer}
                  onChange={() => setOverrideCustomerPrimer(!overrideCustomerPrimer)}
                ></input>
                <label className="text-sm text-gray-500">Override customer provided primers</label>
              </div>
            </>
          )}

          {/* `w-0 min-w-full` keeps this row out of the dialog's `w-fit` measurement: the dialog is as
              wide as its widest child, and without this the button row -- not the form -- decides that. */}
          <div className="modal-action w-0 min-w-full">
            <div className="flex flex-wrap items-center justify-end gap-2 w-full">
              <button className="btn btn-outline btn-sm mr-auto" onClick={() => window.open('/metadata-manual', '_blank')}>
                Metadata Manual
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => props.onClose()}>
                Cancel
              </button>
              {/* Both primary, and each named for what it does rather than one being the absence of the
                  other: the choice is a real one, and neither is the safe default to click past. */}
              <button
                disabled={submitDisabled}
                className="btn btn-primary btn-sm"
                data-test-id="csv-btn-submit"
                onClick={() => handleOKClicked(false)}
              >
                <TooltipTrigger tooltip={'Writes only where nothing is stored yet. Every value already on record is left as it is.'}>
                  Import &amp; fill gaps
                </TooltipTrigger>
              </button>
              <button
                disabled={submitDisabled}
                className="btn btn-primary btn-sm"
                data-test-id="csv-btn-submit-override"
                onClick={() => handleOKClicked(true)}
              >
                <TooltipTrigger
                  tooltip={`Writes wherever the file has a value, replacing what is on record, and applies ${CLEAR_SENTINEL} cells as clears.`}
                >
                  Import &amp; overwrite
                </TooltipTrigger>
              </button>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  )
}
